import { DownOutlined, LinkOutlined } from '@ant-design/icons'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { ReadableDifference, ShowRunOutWarningClass, TankHasWarning } from '@utils/general'
import { Card } from 'antd'
import { useEffect, useState } from 'react'

import { TankFillBar } from './TankFillBar'
import { TankProductRow } from './TankProductRow'
import { createManifoldAggregate, getTankSelected } from './util'
import { StorageDetailVolume } from './StorageDetailVolume'
import type { ProductGroupsApiType } from '@/components/OrderWizard/Contexts/OrderContainersContext/ProductGroups/useProductGroups'

type ManifoldGroupProps = {
  rundownToggled?: boolean
  manifold: Gravitate.Site['grouped_tanks'][number]
  selectedManifold?: Gravitate.Site['grouped_tanks'][number]
  setSelectedManifold: (manifold: Gravitate.Site['grouped_tanks'][number] | undefined) => void
  setSelectedTank: (tank: Gravitate.Tank | undefined) => void
  selectedTank: Gravitate.Tank | undefined
  onOrder?: boolean
  addFunction?: (tank: Gravitate.Tank) => void
  disabled?: boolean
  visibleTankIds: number[]
  currentEntryMode?: 'by_compartment' | 'by_product'
  productGroupsApi?: ProductGroupsApiType
  callInSite?: boolean
}

export function ManifoldGroup({
  rundownToggled,
  manifold,
  selectedManifold,
  setSelectedManifold,
  setSelectedTank,
  selectedTank,
  onOrder = false,
  addFunction,
  disabled,
  visibleTankIds,
  currentEntryMode,
  productGroupsApi,
  callInSite,
}: ManifoldGroupProps) {
  const [showingLinkedTanks, setShowingLinkedTanks] = useState(true)
  const [manifoldTank, setManifoldTank] = useState()
  const isSelected = selectedManifold?.manifold_id === manifold.manifold_id
  const projectedVolume = manifold.tanks.some((item) => !!item.projected_volume)
    ? manifold.tanks.map((item) => item.projected_volume || item.current_volume).reduce((prev, next) => prev + next)
    : null

  useEffect(() => {
    setManifoldTank(createManifoldAggregate(manifold))
  }, [manifold.tanks])

  if (!manifoldTank) return null

  const runoutDate = rundownToggled ? manifold.run_out_date_with_drops : manifold.run_out_date

  return (
    <div className='my-2'>
      <Card
        size='small'
        className={`mx-2 store-details-product-container ${isSelected ? 'selected' : ''} manifold-product-container`}
        actions={[
          <TanksActionBar
            key='tanks-action-bar'
            showingLinkedTanks={showingLinkedTanks}
            setShowingLinkedTanks={setShowingLinkedTanks}
            manifold={manifold}
          />,
        ]}
      >
        <Vertical
          onClick={() => {
            setSelectedManifold(manifoldTank)
            setSelectedTank(undefined)
          }}
          flex='auto'
        >
          <Horizontal>
            {!callInSite ? (
              <Horizontal verticalCenter justifyContent='space-between' width='100%'>
                <Texto className='text-ellipsis' category='p2' weight='bold' width='100%'>
                  {manifold.manifold_id}
                </Texto>
              </Horizontal>
            ) : (
              <>
                <Vertical className={`store-detail-product-title ${onOrder ? ' order' : ''}`} flex={4} verticalCenter>
                  <Texto className='text-ellipsis' appearance='secondary'>
                    {manifold.manifold_id}
                  </Texto>
                </Vertical>
                <Vertical flex={1} verticalCenter horizontalCenter>
                  <Texto appearance={'hint'}>Tank Size</Texto>
                  <Texto textTransform={'uppercase'}>{addCommasToNumber(manifoldTank.storage_max, 0)}</Texto>
                </Vertical>
              </>
            )}
          </Horizontal>
          <Vertical>
            <Horizontal justifyContent='space-between' verticalCenter>
              <Texto weight='bold' category='p1'>
                Manifold
              </Texto>
              {!callInSite && (
                <Texto className={`text-ellipsis ${ShowRunOutWarningClass(runoutDate)}`}>
                  {ReadableDifference(runoutDate, undefined, true)}
                </Texto>
              )}
            </Horizontal>
            {!callInSite && (
              <Vertical>
                <TankFillBar
                  fill={manifoldTank.percent_filled}
                  showWarning={runoutDate ? TankHasWarning(runoutDate) : false}
                  projectedFill={projectedVolume / manifoldTank.storage_max}
                  lowThreshold={0.5}
                />
                <StorageDetailVolume
                  current_volume={addCommasToNumber(manifoldTank.current_volume, 0)}
                  projected_volume={projectedVolume && addCommasToNumber(projectedVolume, 0)}
                  storage_max={addCommasToNumber(manifoldTank.storage_max, 0)}
                />
              </Vertical>
            )}
          </Vertical>
        </Vertical>
      </Card>
      <div className='manifold-group'>
        {showingLinkedTanks &&
          manifold.tanks
            .filter((tank) => visibleTankIds.includes(tank.tank_id))
            .map((tank) => (
              <TankProductRow
                isManifold
                rundownToggled={rundownToggled}
                key={tank.tank_id}
                tank={tank}
                isSelected={tank.tank_id === selectedTank?.tank_id && tank.store_number === selectedTank?.store_number}
                setSelectedTank={(tank) => {
                  selectedManifold && setSelectedManifold(undefined)
                  setSelectedTank(tank)
                }}
                addFunction={addFunction && (() => addFunction(tank))}
                disabled={disabled}
                currentEntryMode={currentEntryMode}
                productGroupsApi={productGroupsApi}
                callInSite={callInSite}
              />
            ))}
      </div>
    </div>
  )
}

function TanksActionBar({
  showingLinkedTanks,
  setShowingLinkedTanks,
  manifold,
}: {
  showingLinkedTanks: boolean
  setShowingLinkedTanks: (showing: boolean) => void
  manifold: Gravitate.Site['grouped_tanks'][number]
}) {
  return (
    <Horizontal border='all' className='bg-2 pt-1 pl-2' justifyContent='space-between'>
      <Texto weight='bold'>
        <LinkOutlined className='pr-1' />
        {`Tanks (${manifold.tanks?.length})`}
      </Texto>
      <GraviButton
        appearance='text'
        className='expand-icon-container'
        onClick={() => setShowingLinkedTanks(!showingLinkedTanks)}
      >
        <DownOutlined className={`expand-icon ${showingLinkedTanks ? 'rotated' : ''}`} />
      </GraviButton>
    </Horizontal>
  )
}
